import React, { useEffect, useState } from "react";
import { Euro, Loader2, Settings2, Send, CheckCircle2, Download, FileText, Layers, ListChecks, Bell, AlertTriangle, Wallet, Zap, MousePointerClick, Clock3, GitBranch } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TagInsights } from "@/components/TagInsights";
import { UnsubscribesReport } from "@/components/UnsubscribesReport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const lastMonth = () => {
  const d = new Date();
  const s = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const e = new Date(d.getFullYear(), d.getMonth(), 0);
  return { start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) };
};
const eur = (n) => `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRESETS = [
  { id: "month", label: "Questo mese", range: () => ({ start: monthStart(), end: todayISO() }) },
  { id: "last", label: "Mese scorso", range: lastMonth },
  { id: "7", label: "Ultimi 7 giorni", range: () => ({ start: addDays(-7), end: todayISO() }) },
  { id: "30", label: "Ultimi 30 giorni", range: () => ({ start: addDays(-30), end: todayISO() }) },
  { id: "90", label: "Ultimi 90 giorni", range: () => ({ start: addDays(-90), end: todayISO() }) },
];

export default function Report() {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState({ start: monthStart(), end: todayISO() });
  const [data, setData] = useState(null);
  const [campaignRows, setCampaignRows] = useState([]);
  const [targets, setTargets] = useState({ by_cluster: [], by_list: [] });
  const [links, setLinks] = useState(null);
  const [campClicks, setCampClicks] = useState([]);
  const [bestTimes, setBestTimes] = useState(null);
  const [metaCosts, setMetaCosts] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (r) => {
    setLoading(true);
    try {
      const [m, c, t, b, l, cc, bt, mc] = await Promise.all([
        api.get("/reports/messages", { params: { start: r.start, end: r.end } }),
        api.get("/reports/campaigns", { params: { start: r.start, end: r.end } }),
        api.get("/reports/by-target", { params: { start: r.start, end: r.end } }),
        api.get("/reports/budget"),
        api.get("/reports/links", { params: { start: r.start, end: r.end } }),
        api.get("/reports/campaign-clicks", { params: { start: r.start, end: r.end } }),
        api.get("/reports/best-times", { params: { start: r.start, end: r.end } }),
        api.get("/reports/meta-costs", { params: { start: r.start, end: r.end } }).catch(() => ({ data: { available: false } })),
      ]);
      setData(m.data);
      setCampaignRows(c.data);
      setTargets(t.data);
      setBudget(b.data);
      setLinks(l.data);
      setCampClicks(cc.data || []);
      setBestTimes(bt.data);
      setMetaCosts(mc.data);
    } catch {
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    setPreset(p.id);
    const r = p.range();
    setRange(r);
    load(r);
  };
  const applyCustom = () => { setPreset("custom"); load(range); };

  const exportReport = async (format) => {
    try {
      const res = await api.get("/reports/export", { params: { start: range.start, end: range.end, format }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-costi-${range.start}_${range.end}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Report ${format.toUpperCase()} scaricato`);
    } catch {
      toast.error("Errore durante l'esportazione");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Report & Costi Meta</h1>
          <p className="text-slate-400 text-sm mt-1">Messaggi inviati e recapitati con i costi reali addebitati da Meta, per tipologia e periodo</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" data-testid="report-export-csv-button" onClick={() => exportReport("csv")}
                  className="bg-[#1E293B] border-[#1F2937] text-slate-200 hover:bg-[#334155]">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" data-testid="report-export-pdf-button" onClick={() => exportReport("pdf")}
                  className="bg-[#1E293B] border-[#1F2937] text-slate-200 hover:bg-[#334155]">
            <FileText className="h-4 w-4 mr-2" /> PDF
          </Button>
          <PricingDialog onDone={() => load(range)} />
          <ClientReportDialog />
          <ReminderDialog />
          <BudgetDialog budget={budget} onDone={() => load(range)} />
        </div>
      </div>

      {budget && budget.exceeded && (
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="report-budget-alert">
          <AlertTriangle className="h-5 w-5 text-[#EF4444] shrink-0" />
          <p className="text-sm text-[#FCA5A5]">
            Budget superato: spesa {budget.source === "meta" ? "reale Meta" : "stimata"} del mese <strong>{eur(budget.month_cost)}</strong> oltre la soglia di <strong>{eur(budget.threshold)}</strong> ({budget.percent}%).
          </p>
        </div>
      )}

      {metaCosts && !metaCosts.available && (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl px-4 py-3 text-sm text-[#F59E0B]" data-testid="report-demo-notice">
          Costi reali Meta non disponibili ({metaCosts.reason || "account non collegato"}): i valori sotto sono una stima basata sul listino configurato.
        </div>
      )}
      {metaCosts?.available && <MetaCostsPanel mc={metaCosts} estimate={data?.totals?.cost || 0} />}

      {/* Period controls */}
      <div className="flex flex-wrap items-end gap-2">
        {PRESETS.map((p) => (
          <button key={p.id} onClick={() => applyPreset(p)} data-testid={`period-${p.id}`}
                  className={`px-3 py-2 rounded-lg text-sm border transition-all ${
                    preset === p.id ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]" : "border-[#1F2937] text-slate-400 hover:border-[#10B981]/40"}`}>
            {p.label}
          </button>
        ))}
        <div className="flex items-end gap-2 ml-auto flex-wrap">
          <div><Label className="text-xs text-slate-500">Dal</Label>
            <Input type="date" data-testid="report-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })}
                   className="bg-[#111827] border-[#1F2937] text-slate-100 h-9" /></div>
          <div><Label className="text-xs text-slate-500">Al</Label>
            <Input type="date" data-testid="report-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })}
                   className="bg-[#111827] border-[#1F2937] text-slate-100 h-9" /></div>
          <Button onClick={applyCustom} data-testid="report-apply" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold h-9">Applica</Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Send} color="#3B82F6" label="Messaggi inviati" value={loading ? "…" : data?.totals.sent.toLocaleString("it-IT")} testid="total-sent" />
        <StatCard icon={CheckCircle2} color="#10B981" label="Recapitati" value={loading ? "…" : data?.totals.delivered.toLocaleString("it-IT")} testid="total-delivered" />
        <StatCard icon={Euro} color="#F59E0B" label="Costo stimato Meta" value={loading ? "…" : eur(data?.totals.cost || 0)} testid="total-cost" />
      </div>

      {/* Table */}
      <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1F2937] text-slate-500 text-left">
                <th className="px-5 py-3 font-medium">Tipologia</th>
                <th className="px-5 py-3 font-medium text-right">Inviati</th>
                <th className="px-5 py-3 font-medium text-right">Recapitati</th>
                <th className="px-5 py-3 font-medium text-right">Prezzo unitario</th>
                <th className="px-5 py-3 font-medium text-right">Costo</th>
              </tr>
            </thead>
            <tbody data-testid="report-table">
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : (data?.by_category || []).map((r) => (
                <tr key={r.category} className="border-b border-[#1F2937]/60" data-testid={`report-row-${r.category}`}>
                  <td className="px-5 py-3 text-slate-200">{r.label}</td>
                  <td className="px-5 py-3 text-right text-slate-300">{r.sent.toLocaleString("it-IT")}</td>
                  <td className="px-5 py-3 text-right text-[#10B981]">{r.delivered.toLocaleString("it-IT")}</td>
                  <td className="px-5 py-3 text-right text-slate-400">{eur(r.unit_price)}</td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-100">{eur(r.cost)}</td>
                </tr>
              ))}
              {!loading && data && (
                <tr className="bg-[#0A0E17]">
                  <td className="px-5 py-3 font-semibold text-slate-200">Totale</td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-200">{data.totals.sent.toLocaleString("it-IT")}</td>
                  <td className="px-5 py-3 text-right font-semibold text-[#10B981]">{data.totals.delivered.toLocaleString("it-IT")}</td>
                  <td className="px-5 py-3"></td>
                  <td className="px-5 py-3 text-right font-semibold text-[#F59E0B]">{eur(data.totals.cost)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {campaignRows.length > 0 && (
        <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#1F2937]"><h2 className="text-sm font-semibold text-slate-200">Dettaglio per campagna</h2></div>
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead>
            <tr className="border-b border-[#1F2937] text-slate-500 text-left">
              <th className="px-5 py-3 font-medium">Campagna</th><th className="px-5 py-3 font-medium">Tipologia</th>
              <th className="px-5 py-3 font-medium text-right">Inviati</th><th className="px-5 py-3 font-medium text-right">Recapitati</th>
              <th className="px-5 py-3 font-medium text-right">Letti</th><th className="px-5 py-3 font-medium text-right">Costo</th>
            </tr></thead><tbody data-testid="report-campaigns-table">
            {campaignRows.map((r) => (
              <tr key={r.id} className="border-b border-[#1F2937]/60" data-testid={`report-campaign-${r.id}`}>
                <td className="px-5 py-3 text-slate-200">{r.name}</td>
                <td className="px-5 py-3 text-slate-400">{r.label}</td>
                <td className="px-5 py-3 text-right text-slate-300">{r.sent.toLocaleString("it-IT")}</td>
                <td className="px-5 py-3 text-right text-[#10B981]">{r.delivered.toLocaleString("it-IT")}</td>
                <td className="px-5 py-3 text-right text-slate-400">{r.read.toLocaleString("it-IT")}</td>
                <td className="px-5 py-3 text-right font-semibold text-slate-100">{eur(r.cost)}</td>
              </tr>))}
          </tbody></table></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TargetTable icon={Layers} title="Per cluster" rows={targets.by_cluster} loading={loading} testid="report-cluster-table" prefix="cluster" />
        <TargetTable icon={ListChecks} title="Per lista" rows={targets.by_list} loading={loading} testid="report-list-table" prefix="list" />
      </div>

      <LinksReport links={links} loading={loading} />
      <CampaignClickCompare rows={campClicks} loading={loading} />
      <UnsubscribesReport range={range} />
      <ChainCompare range={range} />
      <BestTimes data={bestTimes} loading={loading} />

      <AutomationReport />
      <TagInsights />
    </div>
  );
}

const DAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const CAT_IT = { MARKETING: "Marketing", UTILITY: "Utility", AUTHENTICATION: "Autenticazione", SERVICE: "Servizio (risposte in chat)" };

function MetaCostsPanel({ mc, estimate }) {
  const eurX = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v || 0);
  const max = Math.max(0.0001, ...(mc.by_day || []).map((d) => d.cost));
  return (
    <div className="bg-[#111827]/80 border border-[#10B981]/40 rounded-2xl overflow-hidden" data-testid="report-meta-costs">
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><Euro className="h-4 w-4 text-[#10B981]" /><h2 className="text-sm font-semibold text-slate-200">Costi reali addebitati da Meta</h2>
          <span className="text-[11px] text-slate-500">letti in diretta dal tuo account WhatsApp Business (Cloud API)</span></div>
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span>Messaggi conteggiati: <b className="text-slate-200">{mc.total_volume}</b></span>
          <span>Totale reale: <b className="text-[#10B981] text-sm" data-testid="meta-costs-total">{eurX(mc.total_cost)}</b></span>
          <span className="hidden sm:inline">Stima listino: <b className="text-slate-300">{eurX(estimate)}</b></span>
        </div>
      </div>
      <div className="p-4 grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 overflow-x-auto"><table className="w-full text-xs"><thead>
          <tr className="text-slate-500 text-left border-b border-[#1F2937]"><th className="px-2 py-2 font-medium">Categoria</th><th className="px-2 py-2 text-right font-medium">Msg</th><th className="px-2 py-2 text-right font-medium">Gratuiti</th><th className="px-2 py-2 text-right font-medium">€/msg</th><th className="px-2 py-2 text-right font-medium">Costo</th></tr></thead>
          <tbody data-testid="meta-costs-by-category">
            {(mc.by_category || []).map((c) => (
              <tr key={c.category} className="border-b border-[#1F2937]/60">
                <td className="px-2 py-2 text-slate-200">{CAT_IT[c.category] || c.category}</td>
                <td className="px-2 py-2 text-right text-slate-300">{c.volume}</td>
                <td className="px-2 py-2 text-right text-slate-500">{c.free || 0}</td>
                <td className="px-2 py-2 text-right text-slate-400">{c.unit_cost ? eurX(c.unit_cost) : "—"}</td>
                <td className="px-2 py-2 text-right text-[#10B981] font-semibold">{eurX(c.cost)}</td>
              </tr>
            ))}
            {!(mc.by_category || []).length && <tr><td colSpan={5} className="px-2 py-4 text-center text-slate-500">Nessun addebito nel periodo</td></tr>}
          </tbody></table></div>
        <div className="lg:col-span-3">
          <p className="text-[11px] text-slate-500 mb-1">Costo per giorno</p>
          <div className="flex items-end gap-1 h-28" data-testid="meta-costs-by-day">
            {(mc.by_day || []).map((d) => (
              <div key={d.day} className="flex-1 min-w-[10px] flex flex-col items-center gap-1" title={`${d.day}: ${eurX(d.cost)} · ${d.volume} msg`}>
                <div className="w-full rounded-t bg-[#10B981]/70" style={{ height: `${Math.max(2, (d.cost / max) * 90)}px` }} />
                <span className="text-[9px] text-slate-500">{d.day.slice(8)}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Le risposte libere in chat entro 24h (categoria Servizio) sono gratuite; i template marketing/utility sono addebitati per messaggio recapitato. I dati Meta possono avere un ritardo di qualche ora.</p>
        </div>
      </div>
    </div>
  );
}
const DAYS_FULL = ["lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];

function BestTimes({ data, loading }) {
  const [metric, setMetric] = useState("clicks");
  const m = data?.[metric];
  const grid = m?.grid || [];
  const max = Math.max(1, ...grid.flat());
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const label = metric === "clicks" ? "clic" : "risposte in chat";
  const tip = m && m.best_day !== null && m.best_day !== undefined && m.best_hour !== null
    ? `I tuoi clienti ${metric === "clicks" ? "cliccano" : "rispondono"} di più il ${DAYS_FULL[m.best_day]} e nella fascia ${String(m.best_hour).padStart(2, "0")}:00–${String((m.best_hour + 1) % 24).padStart(2, "0")}:00${m.best_cell ? ` (picco: ${DAYS_FULL[m.best_cell.day]} alle ${String(m.best_cell.hour).padStart(2, "0")}:00, ${m.best_cell.count} ${label})` : ""}. Programma gli invii poco prima di quell'orario.`
    : null;
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden" data-testid="report-best-times-wrap">
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-[#F59E0B]" /><h2 className="text-sm font-semibold text-slate-200">Orari migliori</h2>
          <span className="text-[11px] text-slate-500">giorno × ora (ora italiana) · {m?.total || 0} {label}</span></div>
        <div className="flex gap-1">
          {[["clicks", "Clic sui link"], ["replies", "Risposte in chat"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMetric(k)} data-testid={`best-times-metric-${k}`}
                    className={`px-2.5 py-1 rounded-full text-xs border ${metric === k ? "bg-[#F59E0B] text-[#0A0E17] border-[#F59E0B]" : "border-[#1F2937] text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="px-5 py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : !m || m.total === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">Nessun dato nel periodo: la mappa si riempie con i {label} ricevuti.</p>
      ) : (
        <div className="p-4 space-y-3">
          {tip && <p className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg px-3 py-2" data-testid="best-times-tip">{tip}</p>}
          <div className="overflow-x-auto">
            <div className="min-w-[640px]">
              <div className="grid gap-[3px]" style={{ gridTemplateColumns: "44px repeat(24, minmax(0, 1fr))" }} data-testid="best-times-heatmap">
                <div />
                {hours.map((h) => <div key={h} className="text-[9px] text-slate-500 text-center">{h % 3 === 0 ? `${h}` : ""}</div>)}
                {grid.map((row, d) => (
                  <React.Fragment key={d}>
                    <div className={`text-[11px] pr-1 flex items-center ${m.best_day === d ? "text-[#F59E0B] font-semibold" : "text-slate-400"}`}>{DAYS_IT[d]}</div>
                    {row.map((v, h) => {
                      const a = v / max;
                      const isBest = m.best_cell && m.best_cell.day === d && m.best_cell.hour === h;
                      return (
                        <div key={h} title={`${DAYS_FULL[d]} ${String(h).padStart(2, "0")}:00 — ${v} ${label}`}
                             className={`h-5 rounded-[3px] ${isBest ? "ring-2 ring-[#F59E0B]" : ""}`}
                             style={{ backgroundColor: v === 0 ? "#0B1120" : `rgba(245, 158, 11, ${0.18 + a * 0.82})` }} />
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500"><span>Meno</span>
                {[0.18, 0.4, 0.6, 0.8, 1].map((a) => <span key={a} className="h-3 w-5 rounded-[2px]" style={{ backgroundColor: `rgba(245, 158, 11, ${a})` }} />)}<span>Più</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignClickCompare({ rows, loading }) {
  const data = (rows || []).slice().reverse().map((r) => ({ ...r, label: r.name.length > 18 ? r.name.slice(0, 17) + "…" : r.name }));
  const best = (rows || []).reduce((a, r) => (a === null || r.click_rate > a.click_rate ? r : a), null);
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden" data-testid="report-campaign-clicks-wrap">
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><MousePointerClick className="h-4 w-4 text-[#F59E0B]" /><h2 className="text-sm font-semibold text-slate-200">Tasso di clic per campagna</h2>
          <span className="text-[11px] text-slate-500">ultime {rows?.length || 0} campagne con link tracciato nel periodo</span></div>
        {best && best.click_rate > 0 && <span className="text-xs text-slate-400" data-testid="report-best-campaign">Migliore: <b className="text-[#10B981]">{best.name}</b> · {best.click_rate}%</span>}
      </div>
      {loading ? (
        <div className="px-5 py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : data.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">Nessuna campagna con link tracciato nel periodo.</p>
      ) : (
        <div className="p-4 grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 h-64" data-testid="report-campaign-clicks-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} interval={0} />
                <YAxis yAxisId="n" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="pct" orientation="right" unit="%" domain={[0, 100]} tick={{ fill: "#10B981", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #1F2937", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#E2E8F0" }}
                         formatter={(v, n) => [n === "Tasso clic" ? `${v}%` : v, n]} labelFormatter={(_, p) => p?.[0]?.payload?.name || ""} />
                <Bar yAxisId="n" dataKey="delivered" name="Consegnati" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                <Bar yAxisId="n" dataKey="clickers" name="Hanno cliccato" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                <Bar yAxisId="pct" dataKey="click_rate" name="Tasso clic" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-2 overflow-x-auto"><table className="w-full text-xs"><thead>
            <tr className="text-slate-500 text-left border-b border-[#1F2937]"><th className="px-2 py-2 font-medium">Campagna</th><th className="px-2 py-2 text-right font-medium">Consegnati</th><th className="px-2 py-2 text-right font-medium">Clic</th><th className="px-2 py-2 text-right font-medium">Senza clic</th><th className="px-2 py-2 text-right font-medium">Tasso</th></tr></thead>
            <tbody data-testid="report-campaign-clicks-table">
              {(rows || []).map((r) => (
                <tr key={r.id} className="border-b border-[#1F2937]/60" data-testid={`report-campaign-click-${r.id}`}>
                  <td className="px-2 py-2 text-slate-200 truncate max-w-[160px]">{r.name}</td>
                  <td className="px-2 py-2 text-right text-[#38BDF8]">{r.delivered}</td>
                  <td className="px-2 py-2 text-right text-[#F59E0B]">{r.clickers}</td>
                  <td className="px-2 py-2 text-right text-slate-400">{r.not_clicked}</td>
                  <td className={`px-2 py-2 text-right font-semibold ${best && best.id === r.id && r.click_rate > 0 ? "text-[#10B981]" : "text-slate-200"}`}>{r.click_rate}%</td>
                </tr>
              ))}
            </tbody></table></div>
        </div>
      )}
    </div>
  );
}

function ChainCompare({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/reports/chains", { params: { start: range.start, end: range.end } }).then((r) => setData(r.data)).catch(() => setData({ rows: [] })).finally(() => setLoading(false));
  }, [range.start, range.end]);
  const rows = data?.rows || [];
  const best = rows.find((r) => r.root_id === data?.best_root_id);
  const eur = (v) => (v == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v));
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden" data-testid="report-chains-wrap">
      <div className="px-5 py-4 border-b border-[#1F2937] flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold text-slate-100 flex items-center gap-2"><GitBranch className="h-5 w-5 text-[#F59E0B]" /> Confronto catene promemoria</h2>
        {best && <span className="text-xs text-slate-400" data-testid="report-best-chain">Migliore: <b className="text-[#10B981]">{best.name}</b> · {best.clicks_per_euro} clic per €</span>}
      </div>
      {loading ? <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#10B981]" /></div> : rows.length === 0 ? (
        <p className="p-5 text-sm text-slate-500" data-testid="report-chains-empty">Nessuna catena di promemoria inviata nel periodo. Attiva «Reinvio a chi non clicca» in una campagna per confrontare qui primo, secondo e terzo invio.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-[#1F2937]">
              <th className="px-5 py-2">Catena</th><th className="px-3 py-2">Invii</th><th className="px-3 py-2">Consegnati</th><th className="px-3 py-2">Hanno cliccato</th><th className="px-3 py-2">Costo</th><th className="px-3 py-2">Clic per €</th><th className="px-3 py-2">€ per clic</th>
            </tr></thead>
            <tbody data-testid="report-chains-table">
              {rows.map((r) => (
                <tr key={r.root_id} className={`border-b border-[#1F2937]/60 ${r.root_id === data.best_root_id ? "bg-[#10B981]/10" : ""}`} data-testid={`report-chain-row-${r.root_id}`}>
                  <td className="px-5 py-2 text-slate-200">
                    <p className="font-medium">{r.root_id === data.best_root_id && <span className="text-[#10B981] mr-1">★</span>}{r.name}</p>
                    <p className="text-[11px] text-slate-500">{r.sent_at ? new Date(r.sent_at).toLocaleDateString("it-IT") : ""} · clic per passaggio: {r.stage_clicks.join(" → ") || "—"}</p>
                  </td>
                  <td className="px-3 py-2 text-slate-300">{r.stages}</td>
                  <td className="px-3 py-2 text-[#38BDF8]">{r.delivered}</td>
                  <td className="px-3 py-2 text-[#F59E0B]">{r.click_contacts} <span className="text-slate-500 text-xs">({r.click_rate}%)</span></td>
                  <td className="px-3 py-2 text-slate-300">{eur(r.cost)}{r.cost != null && <span className="text-[10px] text-slate-500 ml-1">{r.cost_source === "meta" ? "reale" : "stima"}</span>}</td>
                  <td className="px-3 py-2 font-semibold text-[#10B981]">{r.clicks_per_euro ?? "—"}</td>
                  <td className="px-3 py-2 text-slate-300">{r.cost_per_click != null ? eur(r.cost_per_click) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LinksReport({ links, loading }) {
  const rows = links?.by_link || [];
  const t = links?.totals;
  const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden" data-testid="report-links-wrap">
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><MousePointerClick className="h-4 w-4 text-[#F59E0B]" /><h2 className="text-sm font-semibold text-slate-200">Link più cliccati</h2></div>
        {t && (
          <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap" data-testid="report-links-totals">
            <span>Clic: <b className="text-[#F59E0B]">{t.clicks}</b></span>
            <span>Contatti che hanno cliccato: <b className="text-slate-200">{t.unique_contacts}</b></span>
            <span>Recapitati senza clic: <b className="text-slate-200">{t.not_clicked}</b></span>
            <span>Tasso clic: <b className="text-[#10B981]">{pct(t.click_rate)}</b></span>
          </div>
        )}
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm"><thead>
        <tr className="border-b border-[#1F2937] text-slate-500 text-left">
          <th className="px-5 py-3 font-medium">Template</th>
          <th className="px-5 py-3 font-medium">Link</th>
          <th className="px-5 py-3 font-medium text-right">Clic</th>
          <th className="px-5 py-3 font-medium text-right">Contatti</th>
          <th className="px-5 py-3 font-medium text-right">Recapitati</th>
          <th className="px-5 py-3 font-medium text-right">Senza clic</th>
          <th className="px-5 py-3 font-medium text-right">Tasso clic</th>
        </tr></thead><tbody data-testid="report-links-table">
        {loading ? (
          <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500">Nessun clic nel periodo. I clic si rilevano sui template con tracciamento link attivo.</td></tr>
        ) : rows.map((r) => (
          <tr key={r.code} className="border-b border-[#1F2937]/60" data-testid={`report-link-${r.code}`}>
            <td className="px-5 py-3 text-slate-200">{r.template_name}</td>
            <td className="px-5 py-3 text-slate-400 max-w-[260px]"><span className="text-slate-300">{r.label}</span> <span className="truncate block text-xs">{r.url}</span></td>
            <td className="px-5 py-3 text-right font-semibold text-[#F59E0B]">{r.clicks}</td>
            <td className="px-5 py-3 text-right text-slate-300">{r.unique_contacts}</td>
            <td className="px-5 py-3 text-right text-slate-300">{r.delivered_contacts}</td>
            <td className="px-5 py-3 text-right text-slate-400">{r.not_clicked}</td>
            <td className="px-5 py-3 text-right text-[#10B981]">{pct(r.click_rate)}</td>
          </tr>
        ))}
      </tbody></table></div>
    </div>
  );
}

function AutomationReport() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/reports/automations", { params: { days } }).then((r) => { setData(r.data); setSel(r.data.automations[0]?.id || null); }).catch(() => setData({ automations: [] }));
  }, [days]);
  const autos = data?.automations || [];
  const cur = autos.find((a) => a.id === sel) || autos[0];
  const chart = (cur?.drop_off || []).map((d) => ({ name: `Step ${d.step || "?"}`, Abbandoni: d.count, msg: d.message }));
  return (
    <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4" data-testid="automation-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#8B5CF6]" />
          <h2 className="text-lg font-semibold text-slate-100">Automazioni</h2>
          <span className="text-xs text-slate-500">Più attivate e dove i clienti si fermano</span>
        </div>
        <div className="flex gap-1.5">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)} data-testid={`automation-report-days-${d}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${days === d ? "bg-[#8B5CF6]/15 border-[#8B5CF6]/40 text-[#8B5CF6]" : "border-[#1F2937] text-slate-400 hover:text-slate-200"}`}>
              Ultimi {d} giorni
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#8B5CF6]" /></div>
      ) : autos.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="automation-report-empty">Nessuna automazione attivata nel periodo.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-[#1F2937] overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#0A0E17] text-slate-500"><tr><th className="text-left px-3 py-2 font-medium">Automazione</th><th className="text-right px-3 py-2 font-medium">Avvii</th><th className="text-right px-3 py-2 font-medium">Completati</th><th className="text-right px-3 py-2 font-medium">Abbandoni</th></tr></thead>
              <tbody>
                {autos.map((a) => {
                  const rate = a.starts ? Math.round((a.completed / a.starts) * 100) : 0;
                  return (
                    <tr key={a.id} onClick={() => setSel(a.id)} data-testid={`automation-report-row-${a.id}`}
                        className={`border-t border-[#1F2937] cursor-pointer transition-colors ${cur?.id === a.id ? "bg-[#8B5CF6]/10" : "hover:bg-[#1F2937]/40"}`}>
                      <td className="px-3 py-2 text-slate-200">{a.name}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{a.starts}</td>
                      <td className="px-3 py-2 text-right text-[#10B981]">{a.completed} <span className="text-slate-500">({rate}%)</span></td>
                      <td className="px-3 py-2 text-right text-[#F59E0B]">{a.abandons}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl border border-[#1F2937] p-3" data-testid="automation-dropoff-chart">
            <p className="text-xs text-slate-400 mb-2">Abbandoni per step · <span className="text-slate-200">{cur?.name}</span></p>
            {chart.length === 0 ? (
              <p className="text-xs text-slate-500 py-8 text-center">Nessun abbandono registrato: tutti i clienti completano il percorso.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chart} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#1F2937" }} contentStyle={{ background: "#0A0E17", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }}
                           formatter={(v) => [v, "Abbandoni"]} labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.msg || ""}`} />
                  <Bar dataKey="Abbandoni" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function TargetTable({ icon: Icon, title, rows, loading, testid, prefix }) {
  const totalCost = (rows || []).reduce((a, r) => a + r.cost, 0);
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden" data-testid={`${testid}-wrap`}>
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#3B82F6]" />
        <h2 className="text-sm font-semibold text-slate-200">{title}</h2>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm"><thead>
        <tr className="border-b border-[#1F2937] text-slate-500 text-left">
          <th className="px-5 py-3 font-medium">Nome</th>
          <th className="px-5 py-3 font-medium text-right">Inviati</th>
          <th className="px-5 py-3 font-medium text-right">Recapitati</th>
          <th className="px-5 py-3 font-medium text-right">Costo</th>
        </tr></thead><tbody data-testid={testid}>
        {loading ? (
          <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
        ) : (rows || []).length === 0 ? (
          <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">Nessun dato nel periodo</td></tr>
        ) : (
          <>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#1F2937]/60" data-testid={`report-${prefix}-${r.id}`}>
                <td className="px-5 py-3 text-slate-200">{r.name}</td>
                <td className="px-5 py-3 text-right text-slate-300">{r.sent.toLocaleString("it-IT")}</td>
                <td className="px-5 py-3 text-right text-[#10B981]">{r.delivered.toLocaleString("it-IT")}</td>
                <td className="px-5 py-3 text-right font-semibold text-slate-100">{eur(r.cost)}</td>
              </tr>
            ))}
            <tr className="bg-[#0A0E17]">
              <td className="px-5 py-3 font-semibold text-slate-200">Totale</td>
              <td className="px-5 py-3"></td><td className="px-5 py-3"></td>
              <td className="px-5 py-3 text-right font-semibold text-[#F59E0B]">{eur(totalCost)}</td>
            </tr>
          </>
        )}
      </tbody></table></div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 flex items-center gap-4" data-testid={testid}>
      <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-100">{value}</p>
      </div>
    </div>
  );
}

function PricingDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [prices, setPrices] = useState(null);
  const [saving, setSaving] = useState(false);
  const CATS = [
    { k: "marketing", label: "Marketing" },
    { k: "utility", label: "Utility" },
    { k: "service", label: "Servizio" },
    { k: "authentication", label: "OTP / Autenticazione" },
  ];

  useEffect(() => {
    if (open) api.get("/reports/pricing").then((r) => setPrices(r.data.prices)).catch(() => {});
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/reports/pricing", { prices });
      toast.success("Listino aggiornato");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="edit-pricing-button" className="bg-[#1E293B] border-[#10B981]/20 text-[#10B981] hover:bg-[#334155]">
          <Settings2 className="h-4 w-4 mr-2" /> Listino prezzi
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Listino prezzi Meta (demo, € per messaggio)</DialogTitle></DialogHeader>
        {!prices ? <p className="text-slate-500 text-sm py-6 text-center">Caricamento...</p> : (
          <div className="space-y-3">
            {CATS.map((c) => (
              <div key={c.k} className="flex items-center justify-between gap-3">
                <Label className="flex-1">{c.label}</Label>
                <Input type="number" step="0.0001" data-testid={`price-${c.k}`} value={prices[c.k] ?? 0}
                       onChange={(e) => setPrices({ ...prices, [c.k]: parseFloat(e.target.value || "0") })}
                       className="bg-[#0A0E17] border-[#1F2937] w-40 text-right" />
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={saving || !prices} data-testid="save-pricing-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva listino"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReminderDialog() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get("/app-settings").then((r) => setEmail(r.data.reminder_email || "")).catch(() => {}).finally(() => setLoading(false));
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/app-settings", { reminder_email: email });
      toast.success("Email promemoria salvata");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="reminder-email-button" className="bg-[#1E293B] border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#334155]">
          <Bell className="h-4 w-4 mr-2" /> Email promemoria
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Email promemoria campagne</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Il giorno prima di ogni campagna programmata riceverai un'email (alle 08:00 UTC) con anteprima del messaggio, destinatari stimati e costo stimato a Meta.</p>
        <div className="space-y-2">
          <Label className="text-sm">Indirizzo email</Label>
          <Input type="email" data-testid="reminder-email-input" placeholder="nome@azienda.it" value={email}
                 disabled={loading} onChange={(e) => setEmail(e.target.value)}
                 className="bg-[#0A0E17] border-[#1F2937]" />
          <p className="text-xs text-slate-500">Lascia vuoto per usare l'indirizzo admin predefinito.</p>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-reminder-email-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const prevMonthISO = () => { const d = new Date(); const p = new Date(d.getFullYear(), d.getMonth() - 1, 1); return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, "0")}`; };

function Delta({ d, invert }) {
  if (!d || d.delta_pct === null || d.delta_pct === undefined) return <span className="text-slate-600">(n/d)</span>;
  const good = invert ? d.delta_pct <= 0 : d.delta_pct >= 0;
  return <span className={good ? "text-[#10B981]" : "text-[#F87171]"} title={`Mese precedente: ${d.previous}`}>{d.delta_pct > 0 ? "▲" : d.delta_pct < 0 ? "▼" : "="}{Math.abs(d.delta_pct)}%</span>;
}

function ClientBranding({ logoUrl }) {
  const [openB, setOpenB] = useState(false);
  const [f, setF] = useState({ report_client_name: "", report_client_subtitle: "", report_logo_url: "", report_logo_path: "", report_client_email: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (!openB) return;
    api.get("/app-settings").then((r) => setF({ report_client_name: r.data.report_client_name || "", report_client_subtitle: r.data.report_client_subtitle || "", report_logo_url: r.data.report_logo_url || "", report_logo_path: r.data.report_logo_path || "", report_client_email: r.data.report_client_email || "" })).catch(() => {});
  }, [openB]);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.kind !== "image") { toast.error("Carica un'immagine (PNG/JPG)"); return; }
      setF((x) => ({ ...x, report_logo_url: data.url, report_logo_path: data.path }));
      toast.success("Logo caricato: ricordati di salvare");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); } finally { setUploading(false); }
  };
  const save = async () => {
    setSaving(true);
    try { await api.put("/app-settings", f); toast.success("Dati cliente salvati"); setOpenB(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel salvataggio"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-[#1F2937]" data-testid="client-branding-section">
      <button type="button" onClick={() => setOpenB(!openB)} data-testid="client-branding-toggle" className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-300 hover:text-slate-100">
        <span className="flex items-center gap-2"><Settings2 className="h-3.5 w-3.5 text-[#10B981]" /> Dati cliente per il report (nome, logo, email cliente)</span>
        {logoUrl ? <img src={logoUrl} alt="logo" className="h-5 max-w-[80px] object-contain" data-testid="client-branding-logo-preview" /> : <span className="text-slate-500">{openB ? "chiudi" : "modifica"}</span>}
      </button>
      {openB && (
        <div className="px-3 pb-3 space-y-2">
          <Input value={f.report_client_name} onChange={(e) => setF({ ...f, report_client_name: e.target.value })} placeholder="Nome attività / cliente (es. Vivi Club)" data-testid="client-branding-name" className="bg-[#0A0E17] border-[#1F2937] h-8 text-xs" />
          <Input value={f.report_client_subtitle} onChange={(e) => setF({ ...f, report_client_subtitle: e.target.value })} placeholder="Sottotitolo o sito (es. www.viviclub.it)" data-testid="client-branding-subtitle" className="bg-[#0A0E17] border-[#1F2937] h-8 text-xs" />
          <Input type="email" value={f.report_client_email} onChange={(e) => setF({ ...f, report_client_email: e.target.value })} placeholder="Email del cliente (riceve il PDF il 1° del mese, oltre a te)" data-testid="client-branding-email" className="bg-[#0A0E17] border-[#1F2937] h-8 text-xs" />
          <div className="flex items-center gap-3">
            {f.report_logo_url ? <img src={f.report_logo_url} alt="logo" className="h-10 max-w-[140px] object-contain bg-white rounded p-1" data-testid="client-branding-logo" /> : <span className="text-xs text-slate-500">Nessun logo</span>}
            <label className="text-xs text-[#3B82F6] cursor-pointer hover:underline">
              {uploading ? "Caricamento…" : "Carica logo (PNG/JPG)"}
              <input type="file" accept="image/*" className="hidden" data-testid="client-branding-logo-input" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            {f.report_logo_url && <button type="button" className="text-xs text-slate-500 hover:text-[#EF4444]" data-testid="client-branding-logo-remove" onClick={() => setF({ ...f, report_logo_url: "", report_logo_path: "" })}>Rimuovi</button>}
          </div>
          <Button size="sm" onClick={save} disabled={saving} data-testid="client-branding-save" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold h-8 text-xs">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva dati cliente"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ClientReportDialog() {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(prevMonthISO());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || !month) return;
    setData(null);
    api.get("/reports/client", { params: { month } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [open, month]);

  const download = async () => {
    setBusy("pdf");
    try {
      const res = await api.get("/reports/client-pdf", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `report-cliente-${month}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Report cliente scaricato");
    } catch { toast.error("Errore nella generazione del PDF"); } finally { setBusy(""); }
  };
  const sendMail = async () => {
    setBusy("mail");
    try {
      const { data: r } = await api.post("/reports/client-pdf/send", null, { params: { month } });
      toast.success(`Report inviato a ${Array.isArray(r.to) ? r.to.join(", ") : r.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="client-report-button" className="bg-[#1E293B] border-[#10B981]/30 text-[#10B981] hover:bg-[#334155]">
          <FileText className="h-4 w-4 mr-2" /> Report cliente
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Report mensile per il cliente</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">PDF con WhatsApp e SMS del mese: inviati, consegne, letture, clic e costi, con il dettaglio per campagna. Il 1° di ogni mese viene inviato automaticamente all'indirizzo «Email promemoria».</p>
        <div className="space-y-2">
          <Label className="text-sm">Mese</Label>
          <Input type="month" data-testid="client-report-month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#0A0E17] border-[#1F2937]" />
        </div>
        {data && !data.error && (
          <div className="grid grid-cols-2 gap-2 text-xs rounded-xl border border-[#1F2937] p-3" data-testid="client-report-preview">
            <div><p className="text-slate-500">WhatsApp</p><p className="text-slate-200">{data.whatsapp.sent} inviati <Delta d={data.compare["whatsapp.sent"]} /> · {data.whatsapp.delivered} consegnati <Delta d={data.compare["whatsapp.delivered"]} /> · {data.whatsapp.read} letti <Delta d={data.compare["whatsapp.read"]} /></p></div>
            <div><p className="text-slate-500">SMS</p><p className="text-slate-200">{data.sms.sent} inviati <Delta d={data.compare["sms.sent"]} /> · {data.sms.delivered} consegnati · {data.sms.failed} falliti <Delta d={data.compare["sms.failed"]} invert /></p></div>
            <div><p className="text-slate-500">Clic sui link</p><p className="text-slate-200">{data.clicks.total} <Delta d={data.compare["clicks.total"]} /> · {data.clicks.contacts} contatti</p></div>
            <div><p className="text-slate-500">Costo totale</p><p className="text-[#10B981] font-semibold">{eur(data.total_cost)} <Delta d={data.compare.total_cost} invert /> <span className="text-slate-500 font-normal">({data.whatsapp.cost_source})</span></p></div>
            <p className="col-span-2 text-slate-500" data-testid="client-report-compare-note">{data.campaigns.length} campagne nel mese · variazioni rispetto a {data.previous_label}</p>
          </div>
        )}
        {data === null && <div className="py-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-[#10B981]" /></div>}
        <ClientBranding logoUrl={data && !data.error ? data.logo_url : ""} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={sendMail} disabled={!!busy} data-testid="client-report-send-button" className="bg-[#1E293B] border-[#1F2937] text-slate-200 hover:bg-[#334155]">
            {busy === "mail" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Invia via email</>}
          </Button>
          <Button onClick={download} disabled={!!busy} data-testid="client-report-download-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Scarica PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BudgetDialog({ budget, onDone }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [target, setTarget] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/app-settings").then((r) => { setValue(String(r.data.budget_threshold || "")); setTarget(String(r.data.monthly_message_target || "")); }).catch(() => {});
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/app-settings", { budget_threshold: parseFloat(value || "0"), monthly_message_target: parseInt(target || "0", 10) });
      toast.success("Budget e obiettivo mensile salvati");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const exceeded = budget && budget.exceeded;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="budget-button"
                className={`bg-[#1E293B] hover:bg-[#334155] ${exceeded ? "border-[#EF4444]/50 text-[#EF4444]" : "border-[#F59E0B]/30 text-[#F59E0B]"}`}>
          <Wallet className="h-4 w-4 mr-2" /> Budget
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Soglia di budget mensile</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Riceverai un avviso in Dashboard e qui quando la spesa stimata a Meta del mese supera questa soglia. Imposta 0 per disattivare l'avviso.</p>
        {budget && (
          <div className="bg-[#0A0E17] border border-[#1F2937] rounded-lg px-4 py-3 text-sm">
            <p className="text-slate-400">Spesa {budget.source === "meta" ? <span className="text-[#10B981]">reale Meta</span> : "stimata"} del mese: <span className="font-semibold text-slate-100" data-testid="budget-month-cost">{eur(budget.month_cost)}</span></p>
            {budget.threshold > 0 && <p className="text-slate-400 mt-1">Utilizzo soglia: <span className={budget.exceeded ? "text-[#EF4444] font-semibold" : "text-[#10B981] font-semibold"}>{budget.percent}%</span></p>}
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-sm">Soglia mensile (€)</Label>
          <Input type="number" step="0.01" min="0" data-testid="budget-input" placeholder="es. 500" value={value}
                 onChange={(e) => setValue(e.target.value)} className="bg-[#0A0E17] border-[#1F2937]" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Obiettivo messaggi al mese (contatore in Dashboard)</Label>
          <Input type="number" step="1000" min="0" data-testid="monthly-target-input" placeholder="es. 150000" value={target}
                 onChange={(e) => setTarget(e.target.value)} className="bg-[#0A0E17] border-[#1F2937]" />
          <p className="text-[11px] text-slate-500">La Dashboard mostra «inviati / obiettivo» con la proiezione di spesa a fine mese (inviati + campagne programmate).</p>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-budget-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva soglia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
